import { Component, OnDestroy, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { filter, forkJoin, Subject, takeUntil } from 'rxjs';
import { AccountService } from './shared/services/account.service';
import { TranslateService } from '@ngx-translate/core';
import { NzIconService } from 'ng-zorro-antd/icon';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private accountService: AccountService,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private translate: TranslateService,
    private iconService: NzIconService,
    private http: HttpClient
  ) {
    translate.setDefaultLang('fr');
    translate.use('fr');
    this.loadCustomIcons();
  }

  loadCustomIcons(): void {
    const iconRequests = [
      { name: 'custom:eye', path: 'assets/icons/table/eye.svg' },
      { name: 'custom:add', path: 'assets/icons/buttons&Badges/add.svg' },
      {
        name: 'custom:pending',
        path: 'assets/icons/buttons&Badges/pending.svg',
      },
      { name: 'custom:delete', path: 'assets/icons/table/delete.svg' },
      { name: 'custom:filters', path: 'assets/icons/table/filters.svg' },
      {
        name: 'custom:deleteFilter',
        path: 'assets/icons/buttons&Badges/deleteFilter.svg',
      },
      {
        name: 'custom:separator',
        path: 'assets/icons/breadcrumbs/separator.svg',
      },
      {
        name: 'custom:back',
        path: 'assets/icons/table/back.svg',
      },
      {
        name: 'custom:forward',
        path: 'assets/icons/table/forward.svg',
      },
      {
        name: 'custom:logOut',
        path: 'assets/icons/navBar/logOut.svg',
      },
      {
        name: 'custom:search',
        path: 'assets/icons/table/search.svg',
      },
      {
        name: 'custom:arrowRight',
        path: 'assets/icons/table/arrowRight.svg',
      },
      {
        name: 'custom:export',
        path: 'assets/icons/buttons&Badges/export.svg',
      },
    ];

    const requests = iconRequests.map((icon) =>
      this.http.get(icon.path, { responseType: 'text' })
    );

    forkJoin(requests).subscribe((responses) => {
      responses.forEach((svgContent, index) => {
        this.iconService.addIconLiteral(iconRequests[index].name, svgContent);
      });
    });
  }

  public ngOnInit(): void {
    // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.authService.instance.enableAccountStorageEvents();

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS)
      )
      .subscribe(() => {
        this._checkAndSetActiveAccount();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe(() => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this._checkAndSetActiveAccount();
      });

    this.accountService.activeAccount$
      .pipe(takeUntil(this._destroying$))
      .subscribe((account) => {
        if (account) {
          this.accountService.setUserRole();
        }
      });
  }

  public ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  private _checkAndSetActiveAccount() {
    const activeAccount = this.authService.instance.getActiveAccount();
    this.accountService.setActiveAccount(activeAccount);

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }
}
